import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Card } from "react-bootstrap";
import SimpleBar from "simplebar-react";

import "../../css/scroll.css";
// components
import BorderSpinner from "../../components/Spinners/BorderSpinner";
import Message from "../../components/chat/Message";
import ChatHeader from "../../components/chat/ChatHeader";
import ChatFooter from "../../components/chat/ChatFooter";
import ModalPreviewMedia from "../../components/modals/ModalPreviewMedia";

// hooks
import useScrolltoLoad from "../../hooks/useScrolltoLoad";

// context
import { ChatContext } from "../../context/chat/ChatContext";
import { AuthContext } from "../../context/AuthContext";
import { SocketContext } from "../../context/SocketContext";

// Utils
import { fetchWithToken } from "../../api/fetch";
import { types } from "../../types/types";
import createPermsObj from "../../utils/permissionsHelper";
import { useNavigate } from "react-router-dom";
const baseURL = process.env.REACT_APP_API;

/*
 * Componente funcional que renderiza el área de chat
 *
 * @returns {JSX.Element} Componente de react
 */
const ChatArea = () => {
  const [replyMsg, setReplyMsg] = useState(null); // Mensaje al que se responde
  const [offset, setOffset] = useState(25); // Offset para cargar más mensajes
  const [hasMore, setHasMore] = useState(true); // Indica si hay más mensajes por cargar
  const [isSearching, setIsSearching] = useState(false); // Indica si se está buscando mensajes
  const [activeModal, setActiveModal] = useState(null); // Mensaje con medios a mostrar
  const [agentInfo, setAgentInfo] = useState({}); // Información de los agentes
  const [firstLoad, setFirstLoad] = useState(true); // Indica si es la primera carga
  const simpleBarRef = useRef(null); // Referencia al contenedor de scroll
  const navigate = useNavigate();
  // Contexto del socket
  const { socket } = useContext(SocketContext);

  // Contexto del historial de mensajes
  const { chatState, dispatch } = useContext(ChatContext);
  const { messages, activeChat } = chatState;
  useEffect(() => {
    console.log("cgatacti", activeChat);
    // Si no hay chat activo y hay un ID por URL, significa que el usuario recargó o entró directo
    if (!chatState.activeChat) {
      console.log("naviagete");
      navigate("/chat/filter", { replace: true });
    }
  }, [activeChat, navigate]);
  // Información del usuario autenticado
  const { auth } = useContext(AuthContext);
  const { permissions } = auth;

  // Permisos de la sección de Chats
  const chatPerms = createPermsObj(permissions, "Gestión de Chats");

  // Funciones para el manejo del modal
  const openModal = (messageId) =>
    setActiveModal(messages.find((msg) => msg.id === messageId));
  const closeModal = () => setActiveModal(null);

  // Cargar más mensajes al hacer scroll
  const loadMoreMessages = async () => {
    if (!hasMore || firstLoad) return;

    const resp = await fetchWithToken(
      `sms/sms/${activeChat.id_user}/${offset}`
    );

    if (resp.data.length <= 0) {
      setHasMore(false);
      return;
    }

    if (resp.ok) {
      dispatch({
        type: types.loadMoreMessages,
        payload: resp.data,
      });

      setOffset(offset + 25);

      // Scroll al inicio del nuevo contenido
      const scrollableElement = simpleBarRef.current?.getScrollElement();
      scrollableElement.scrollTop = 50;
    }
  };

  // Obtener el contenedor de scroll
  const getScrollContainer = useCallback(() => {
    return simpleBarRef.current?.getScrollElement();
  }, []);

  // Hook para cargar más mensajes al hacer scroll
  useScrolltoLoad(loadMoreMessages, getScrollContainer);

  // Cargar mensajes iniciales del chat activo
  useEffect(() => {
    const getData = async () => {
      const data = await fetchWithToken(`sms/sms/${activeChat.id}/0`);

      if (data.ok) {
        dispatch({
          type: types.loadMessages,
          payload: data.data,
        });
      }

      if (data.total <= 0) {
        setHasMore(false);
        return;
      }
    };

    if (activeChat) getData();
  }, [activeChat, dispatch]);

  // Scroll al final del chat, solo se ejecuta la primera vez
  useEffect(() => {
    if (!firstLoad) return;
    const scrollableElement = simpleBarRef.current?.getScrollElement();
    if (scrollableElement && messages.length > 0) {
      scrollableElement.scrollTop = scrollableElement.scrollHeight;
      setFirstLoad(false);
    }
  }, [messages]);

  // Nuevo mensaje
  useEffect(() => {
    const handleNewMessage = (newUser) => {
      if (messages.length > 0 && activeChat && activeChat.id === newUser.id) {
        dispatch({
          type: types.newMessage,
          payload: newUser,
        });
      }
    };

    socket?.on("new-sms", handleNewMessage);

    return () => socket?.off("new-sms", handleNewMessage);
  }, [socket, dispatch, activeChat, messages]);

  // Actualizar el estado de los mensajes
  useEffect(() => {
    const handleUpdateMessage = (updatedMsg) => {
      if (
        messages.find(
          (message) =>
            message.idsms === updatedMsg.idsms ||
            message.id === updatedMsg.idsms
        )
      ) {
        dispatch({
          type: types.updateMessage,
          payload: updatedMsg,
        });
      }
    };

    socket?.on("us-item", handleUpdateMessage);

    return () => socket?.off("us-item", handleUpdateMessage);
  }, [socket, dispatch, messages]);

  // Marcar mensajes como leídos
  useEffect(() => {
    if (activeChat?.smsunread > 0 && activeChat)
      socket?.emit("leidos", activeChat.id_user);
  }, [activeChat, socket]);

  // Reinicia el offset y hasMore al cambiar chat o buscar mensajes
  useEffect(() => {
    setOffset(25);
    setHasMore(true);
    setFirstLoad(true);
    dispatch({
      type: types.clearMessages,
    });
  }, [activeChat, isSearching, dispatch]);

  // Cerrar chat al presionar ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && activeChat)
        dispatch({
          type: types.closeChat,
        });
    };

    window.addEventListener("keydown", handleEsc);

    return () => window.removeEventListener("keydown", handleEsc);
  }, [dispatch, activeChat]);

  // Cargar información del agente
  const fetchAgentData = async (agentId) => {
    // Verifica si los datos del agente ya están cargados
    if (!agentInfo[agentId]) {
      const data = await fetchWithToken(
        `agentes/getAgente`,
        { id_agent: agentId },
        "PUT"
      );

      if (data.agente) {
        // Actualiza el estado con la nueva información del agente
        setAgentInfo((prevInfo) => ({
          ...prevInfo,
          [agentId]: data.agente,
        }));
      }
    }
  };

  return (
    // !STYLE ES POR EL TOPBAR user
    <Card className="chat-area">
      <Card.Body
        className="chat-bg d-flex flex-column border rounded gap-2 p-0"
        style={{
          height: "calc(100vh - 130px)",
          maxHeight: "calc(100vh - 130px)",

          overflow: "hidden", // Asegurarnos de que no haya scroll aquí
        }}
      >
        {/* Encabezado del chat */}
        {chatState.activeChat ? (
          <ChatHeader setIsSearching={setIsSearching} />
        ) : null}

        {/* Historial de mensajes */}
        <div
          style={{ display: "flex", flexDirection: "column", height: "100vh" }}
        >
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column-reverse",
            }}
          >
            {chatPerms.access && (
              <SimpleBar
                style={{
                  maxHeight: "calc(100vh - 305px)",
                  marginBottom: "0px",
                }}
                forceVisible="y"
                autoHide={false}
                ref={simpleBarRef}
              >
                {/* Spinner de carga */}
                {hasMore ? <BorderSpinner /> : null}

                <ul className="conversation-list d-flex flex-column-reverse m-0">
                  {messages.map((message, index) => (
                    <Message
                      key={index}
                      message={message}
                      onReply={setReplyMsg}
                      openModal={openModal}
                      fetchAgentData={fetchAgentData}
                      agentInfo={agentInfo}
                    />
                  ))}
                </ul>
              </SimpleBar>
            )}
          </div>
          {/* Pie del chat */}
          {chatPerms.reply && (
            <ChatFooter reply={replyMsg} setReply={setReplyMsg} />
          )}
        </div>
      </Card.Body>

      {/* Modal de vista previa de multimedia */}
      {activeModal && (
        <ModalPreviewMedia
          show={!!activeModal}
          handleClose={closeModal}
          type={activeModal.type}
          src={
            activeModal.object.includes("http")
              ? activeModal.object
              : `${baseURL}/${activeModal.object}`
          }
        />
      )}
    </Card>
  );
};

export default ChatArea;
